/* eslint-disable fp/no-mutating-methods */
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { captureException } from '@sentry/nextjs'

import authService from '~/services/auth.service'

import accountAnalytics from '~/analytics/account/accountAnalytics'
import appConfig from '~/config/app'

import { CHAT, CHAT_MESSAGE, CHAT_RESPONSE, Event, MODAL_VIEW, Modal } from './events'

export enum ChatEvent {
  SEND = 'send',
  RESPONSE = 'response',
}
const isAuth = (): boolean => {
  return !!authService.getAccessToken()
}

/*
 * this array object is used to prevent page log,
 * key= "page" + url.replaceAll('/', '')
 * value = conditions
 * result = if condition true then prevent the page log.
 */
const preventPageLog = [{ page: '/', condition: isAuth }]

const shouldLog = (url: string): boolean => {
  const preventRule = preventPageLog.filter((pl) => pl.page === url)
  if (preventRule.length > 0 && preventRule[0].condition()) {
    return false
  }
  return true
}

function useGetPageViewProps(): { uri: string; title: string } {
  const router = useRouter()
  const path = router.asPath
  const locale = router.locale

  const [pageProps, setPageProps] = useState({ uri: '', title: '' })

  useEffect(() => {
    const uri = locale !== 'en' ? `/${locale}${path}` : path
    const title = document.title

    setPageProps({ uri, title })
  }, [locale, path])

  return pageProps
}

export function useLogPageView(): void {
  const { title, uri } = useGetPageViewProps()

  useEffect(() => {
    if (!title) return

    if (shouldLog(uri)) {
      setTimeout(() => {
        accountAnalytics.logPageView(uri, title)
      }, 0)
    }
  }, [title, uri])
}

export function logModalView(modal: Modal): void {
  logEvent(MODAL_VIEW(modal))
}

export function logEvent(eventData: Event): void {
  // Legacy sink, separate from AnalyticsEvents_V2.logEvent: pushes straight to dataLayer with no
  // consent check and no BigQuery leg, so it needs its own gate.
  if (appConfig.isSchoolPlatform) return

  // eslint-disable-next-line fp/no-mutating-methods
  try {
    const dataLayer = {
      event: 'GAEvent',
      nonInteraction: false,
      ...eventData,
    }

    window.dataLayer.push(dataLayer)
  } catch (err) {
    captureException(err, { extra: { analytics: 'Failed to send event', event: eventData } })
  }
}

export function logChatEvent(chatEvent: string): void {
  if (chatEvent === ChatEvent.SEND) {
    logEvent(CHAT_MESSAGE)
  } else if (chatEvent === ChatEvent.RESPONSE) {
    logEvent(CHAT_RESPONSE)
  } else {
    logEvent(CHAT(chatEvent))
  }
}

export { LOG_IN, LOG_OUT, AUTH_TOKEN, EventAction } from './events'
